/**
 * NavPreviewTab — ERP Workspace "Nav Preview" tab.
 * Simulates any industry/country view of the nav tree, with inline component preview modal.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { RefreshCw, ChevronRight, ChevronDown, Eye, X } from 'lucide-react';
import { API, authFetch } from '../../lib/api';
import { erpToast } from '../../components/erp/ERPToast';
import { ERPScreenRenderer } from '../../erp/ERPScreenRenderer';

const TYPE_COLORS = { Masters: '#3b82f6', Transactions: '#8b5cf6', Reports: '#f59e0b', Settings: '#06b6d4', Other: '#94a3b8' };

function ScreenPreviewModal({ screen, onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.55)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background:'var(--surface,#fff)', border:'1px solid var(--border)', borderRadius:12, width:'90vw', maxWidth:1100, maxHeight:'88vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 80px rgba(0,0,0,.25)' }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 18px', borderBottom:'1px solid var(--border)', background:'var(--surface-2)', flexShrink:0 }}>
          <Eye size={15} color="#3b82f6" />
          <div>
            <div style={{ fontWeight:700, fontSize:'0.85rem' }}>{screen.ScreenTitle}</div>
            <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', fontFamily:'monospace' }}>{screen.ScreenId} · {screen.ComponentPath}</div>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
            <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Rendered via ERPScreenRenderer</span>
            <button onClick={onClose} data-testid="screen-preview-close"
              style={{ background:'none', border:'1px solid var(--border)', borderRadius:6, cursor:'pointer', padding:'3px 10px', fontSize:'0.75rem', color:'var(--text-secondary)' }}>
              <X size={12} style={{ verticalAlign:'middle' }} /> Close
            </button>
          </div>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:16 }}>
          <ERPScreenRenderer componentPath={screen.ComponentPath} />
        </div>
      </div>
    </div>
  );
}

export function NavPreviewTab() {
  const [navData,       setNavData]       = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [expanded,      setExpanded]      = useState(new Set());
  const [simIndustry,   setSimIndustry]   = useState('');
  const [simCountry,    setSimCountry]    = useState('');
  const [industries,    setIndustries]    = useState([]);
  const [countries,     setCountries]     = useState([]);
  const [previewScreen, setPreviewScreen] = useState(null);

  useEffect(() => {
    authFetch(`${API}/erp-workspace/admin/industries`).then(r => r.json()).then(d => { if (Array.isArray(d)) setIndustries(d); }).catch(() => {});
    authFetch(`${API}/erp-workspace/admin/countries`).then(r => r.json()).then(d => { if (Array.isArray(d)) setCountries(d); }).catch(() => {});
  }, []);

  const load = useCallback(async (industry, country) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (industry) params.set('simulateIndustry', industry);
      if (country)  params.set('simulateCountry',  country);
      const r = await authFetch(`${API}/erp-workspace/nav${params.toString() ? `?${params}` : ''}`);
      setNavData(await r.json());
      setExpanded(new Set());
    } catch (e) { erpToast.error(e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { load('', ''); }, [load]);

  const toggle = (key) => setExpanded(s => { const ns = new Set(s); ns.has(key) ? ns.delete(key) : ns.add(key); return ns; });

  const { tree = [], tier, isSuperAdmin, simulating, hiddenByCountry = 0, hiddenByIndustry = 0 } = navData || {};

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Simulation bar */}
      <div style={{ background: simulating ? 'rgba(245,158,11,.07)' : 'var(--surface-2)', border: `1px solid ${simulating ? 'rgba(245,158,11,.3)' : 'var(--border)'}`, borderRadius: 10, padding: '10px 16px', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: simulating ? '#f59e0b' : 'var(--text-secondary)' }}>
          {simulating ? 'Simulating client view:' : 'Preview as:'}
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Industry</span>
          <select value={simIndustry} onChange={e => setSimIndustry(e.target.value)}
            data-testid="nav-preview-sim-industry"
            style={{ fontSize: '0.75rem', padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 5 }}>
            <option value="">— My session (unfiltered) —</option>
            <option value="SHARED">SHARED</option>
            {industries.map(ic => <option key={ic} value={ic}>{ic}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Country</span>
          <select value={simCountry} onChange={e => setSimCountry(e.target.value)}
            data-testid="nav-preview-sim-country"
            style={{ fontSize: '0.75rem', padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 5 }}>
            <option value="">— None —</option>
            {countries.map(cc => <option key={cc} value={cc}>{cc}</option>)}
          </select>
        </div>
        <button className="erp-btn erp-btn-primary" onClick={() => load(simIndustry, simCountry)}
          disabled={loading} data-testid="nav-preview-apply-sim"
          style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: 4 }}>
          {loading ? <RefreshCw size={11} className="spin" /> : <Eye size={11} />} Preview
        </button>
        {simulating && (
          <button className="erp-btn" onClick={() => { setSimIndustry(''); setSimCountry(''); load('', ''); }}
            style={{ fontSize: '0.72rem' }}>
            <X size={10} style={{ marginRight: 3 }} />Reset
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center', fontSize: '0.72rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-muted)' }}>{(tree || []).length} modules</span>
          {tier && <span style={{ color: '#3b82f6', fontWeight: 700 }}>Tier: {tier}</span>}
          {hiddenByCountry  > 0 && <span style={{ background: 'rgba(239,68,68,.1)', color: '#ef4444', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>{hiddenByCountry} screens hidden by country</span>}
          {hiddenByIndustry > 0 && <span style={{ background: 'rgba(245,158,11,.1)', color: '#f59e0b', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>{hiddenByIndustry} modules hidden by industry</span>}
          {!simulating && isSuperAdmin && <span style={{ background: '#fef3c7', color: '#d97706', fontSize: '0.68rem', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>SUPER ADMIN</span>}
        </div>
      </div>

      {loading && <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)' }}>Loading nav tree…</div>}
      {!loading && (tree || []).length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          No modules visible for this simulation. Adjust the filters above.
        </div>
      )}

      {/* Module tree */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(tree || []).map(mod => {
            const modKey    = `m-${mod.ModuleId}`;
            const modOpen   = expanded.has(modKey);
            const totalScreens = (mod.subModules || []).reduce((n, s) => n + Object.values(s.screensByType || {}).reduce((a, b) => a + b.length, 0), 0);
            return (
              <div key={mod.ModuleId} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div onClick={() => toggle(modKey)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--surface-2,#f8fafc)', cursor: 'pointer', userSelect: 'none' }}>
                  {modOpen ? <ChevronDown size={14} color="var(--text-muted)" /> : <ChevronRight size={14} color="var(--text-muted)" />}
                  <span style={{ fontWeight: 700, fontSize: '0.82rem' }}>{mod.ModuleName}</span>
                  <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>#{mod.ModuleId}</span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{mod.subModules?.length || 0} sub-modules · {totalScreens} screens</span>
                </div>

                {modOpen && (mod.subModules || []).map(sub => {
                  const subKey     = `s-${sub.SubModuleId}`;
                  const subOpen    = expanded.has(subKey);
                  const typeKeys   = Object.keys(sub.screensByType || {});
                  const subScreens = typeKeys.reduce((n, t) => n + (sub.screensByType[t]?.length || 0), 0);
                  return (
                    <div key={sub.SubModuleId} style={{ borderTop: '1px solid var(--border)', background: '#fff' }}>
                      <div onClick={() => toggle(subKey)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px 6px 32px', cursor: 'pointer', userSelect: 'none' }}>
                        {subOpen ? <ChevronDown size={12} color="var(--text-muted)" /> : <ChevronRight size={12} color="var(--text-muted)" />}
                        <span style={{ fontWeight: 600, fontSize: '0.79rem' }}>{sub.SubModuleName}</span>
                        <span style={{ fontSize: '0.67rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>({sub.SubModuleId})</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.69rem', color: 'var(--text-muted)' }}>{subScreens} screens</span>
                      </div>

                      {subOpen && typeKeys.map(stype => (
                        <div key={stype} style={{ paddingLeft: 52, paddingBottom: 8 }}>
                          <div style={{ fontSize: '0.67rem', fontWeight: 700, color: TYPE_COLORS[stype] || '#94a3b8', textTransform: 'uppercase', letterSpacing: '.05em', padding: '4px 0 2px' }}>{stype}</div>
                          {(sub.screensByType[stype] || []).map(sc => (
                            <div key={sc.ScreenId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}>
                              <span style={{ fontSize: '0.75rem', color: sc.IsImplemented ? 'var(--text-primary)' : 'var(--text-muted)', flexShrink: 0 }}>{sc.ScreenTitle || sc.ScreenMenuName}</span>
                              <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', color: 'var(--text-muted)' }}>{sc.ScreenId}</span>
                              {sc.CountryCode && <span style={{ fontSize: '0.62rem', background: 'rgba(6,182,212,.08)', color: '#06b6d4', padding: '0 4px', borderRadius: 3 }}>{sc.CountryCode}</span>}
                              {sc.ComponentPath && <span style={{ fontSize: '0.63rem', fontFamily: 'monospace', color: '#3b82f6', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sc.ComponentPath}</span>}
                              {sc.IsImplemented && <span style={{ fontSize: '0.62rem', background: 'rgba(34,197,94,.1)', color: '#22c55e', padding: '0 4px', borderRadius: 3, flexShrink: 0 }}>mapped</span>}
                              {sc.ComponentPath && (
                                <button onClick={() => setPreviewScreen(sc)}
                                  data-testid={`nav-preview-screen-btn-${sc.ScreenId}`}
                                  style={{ marginLeft: 'auto', background: 'rgba(59,130,246,.08)', border: '1px solid rgba(59,130,246,.2)', borderRadius: 4, color: '#3b82f6', cursor: 'pointer', fontSize: '0.65rem', padding: '1px 7px', flexShrink: 0 }}>
                                  <Eye size={9} style={{ verticalAlign: 'middle', marginRight: 3 }} />Preview
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {previewScreen && <ScreenPreviewModal screen={previewScreen} onClose={() => setPreviewScreen(null)} />}
    </div>
  );
}
